import { createContext, useCallback, useContext, useEffect, useState, type PropsWithChildren } from 'react';
import { useLocation } from 'react-router-dom';
import { useActiveWorkout } from '@/hooks/useActiveWorkout';
import { hasSeenInstallGuide, rememberInstallGuide, useInstallState, type InstallResult } from './installPrompt';
import InstallGuideSheet from './InstallGuideSheet';

type OpenOptions = { installation?: Promise<InstallResult>; trigger?: HTMLElement };
const InstallGuideContext = createContext<((options?: OpenOptions) => void) | null>(null);
export function useOpenInstallGuide() {
  const open = useContext(InstallGuideContext);
  if (!open) throw new Error('Install guide must be inside its provider');
  return open;
}
type OpenSheet = { path: string; trigger: Element | null; installation?: Promise<InstallResult> };
export default function InstallGuideProvider({ children }: PropsWithChildren) {
  const { pathname } = useLocation();
  const { activeWorkout } = useActiveWorkout();
  const { installed } = useInstallState();
  const [sheet, setSheet] = useState<OpenSheet | null>(null);
  const open = useCallback((options?: OpenOptions) => {
    rememberInstallGuide();
    setSheet({ path: pathname, trigger: options?.trigger ?? document.activeElement, installation: options?.installation });
  }, [pathname]);
  useEffect(() => { setSheet(null); }, [pathname]);
  useEffect(() => {
    if (pathname !== '/' || activeWorkout.active || installed || hasSeenInstallGuide()) return;
    // Desktop keeps a manual entry; a narrow desktop window must not count as a phone.
    if (!/Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent)) return;
    let timer: number;
    const tryOpen = () => {
      if (hasSeenInstallGuide()) return;
      const editing = document.activeElement?.matches('input, textarea, select, [contenteditable="true"]');
      if (document.visibilityState !== 'visible' || editing || document.querySelector('[role="dialog"], [role="alertdialog"]')) {
        timer = window.setTimeout(tryOpen, 1000);
        return;
      }
      open();
    };
    timer = window.setTimeout(tryOpen, 3000);
    return () => window.clearTimeout(timer);
  }, [pathname, activeWorkout.active, installed, open]);
  return <InstallGuideContext.Provider value={open}>
    {children}
    {sheet?.path === pathname && <InstallGuideSheet trigger={sheet.trigger} installation={sheet.installation} onClose={() => setSheet(null)} />}
  </InstallGuideContext.Provider>;
}
